import { useState, useCallback, useRef } from "react";
import type { StartWebRTCContactCommandOutput } from "@aws-sdk/client-connect";
import log from "../util/logger";

type CallParameter = {
    access_key: string;
    secret_key: string;
    region: string;
    contact_id: string;
    instance_id: string;
    service_type: string;
    group_name: string;
    phone_number: string;
};

// 動的importの結果をモジュール変数でキャッシュ
let ChimeMod: null | typeof import("amazon-chime-sdk-js") = null;
let AwsMod: null | typeof import("aws-sdk") = null;
let ConnectMod: null | typeof import("@aws-sdk/client-connect") = null;

export function useConnect() {
    const [isCalling, setIsCalling] = useState(false);
    const meetingSessionRef = useRef<InstanceType<typeof import("amazon-chime-sdk-js").DefaultMeetingSession> | null>(null); // MeetingSession を保持するための ref

    // 通話開始の関数
    const startWebRTCContact = useCallback(async (callType: string, parameter: string, instance: UnityInstance | undefined) => {
        log(`startWebRTCContact: parameter = ${parameter}`);
        const callParameter: CallParameter = JSON.parse(parameter);

        try {
            // ここでだけ読み込む
            const chimeSdk = ChimeMod ?? (ChimeMod = await import("amazon-chime-sdk-js"));
            const awsSdk = AwsMod ?? (AwsMod = await import("aws-sdk"));
            const connectSdk = ConnectMod ?? (ConnectMod = await import("@aws-sdk/client-connect"));

            // 認証情報を基にインスタンス生成
            const credentials = new awsSdk.Credentials({
                accessKeyId: callParameter.access_key,
                secretAccessKey: callParameter.secret_key,
            });
            const connect = new connectSdk.Connect({
                region: callParameter.region,
                credentials: credentials,
            });

            // 発信
            const participantDetails = {
                DisplayName: "customer",
            };
            const contactInformation: StartWebRTCContactCommandOutput = await connect.startWebRTCContact({
                ContactFlowId: callParameter.contact_id,
                InstanceId: callParameter.instance_id,
                ParticipantDetails: participantDetails,
                Attributes: {
                    callType: callType,
                    phoneNumber: callParameter.phone_number,
                },
            });

            // Chime SDKの会議セッションを設定
            const meetingSessionConfiguration = new chimeSdk.MeetingSessionConfiguration(
                {
                    Meeting: contactInformation.ConnectionData?.Meeting,
                },
                {
                    Attendee: contactInformation.ConnectionData?.Attendee,
                }
            );

            const logger = new chimeSdk.ConsoleLogger("logger", chimeSdk.LogLevel.ERROR);
            const deviceController = new chimeSdk.DefaultDeviceController(logger);
            const meetingSession = new chimeSdk.DefaultMeetingSession(meetingSessionConfiguration, logger, deviceController);

            // 各種コールバック登録
            const audioVideo = meetingSession.audioVideo;
            audioVideo.addObserver({
                // 通話開始
                audioVideoDidStart: () => {
                    log("発信開始");
                    setIsCalling(true);
                },
                // 通話終了
                audioVideoDidStop: () => {
                    log("通話終了");
                    setIsCalling(false);
                    if (instance !== undefined) {
                        instance.SendMessage("GameManager", "DisconnectCall");
                    }
                },
            });

            // デバイスを設定
            await meetingSession.deviceController.startAudioInput("default");
            await meetingSession.deviceController.chooseAudioOutput("default");
            await audioVideo.bindAudioElement(document.getElementById("audio-element") as HTMLAudioElement);
            audioVideo.start();

            // MeetingSession を ref に保存
            meetingSessionRef.current = meetingSession;
        } catch (error) {
            log("通話の開始に失敗しました: ", error);
            setIsCalling(false);
        }
    }, []);

    // 通話終了の関数
    const endWebRTCContact = useCallback(() => {
        if (meetingSessionRef.current) {
            meetingSessionRef.current.audioVideo.stop();
            meetingSessionRef.current.deviceController.stopAudioInput();
            // meetingSessionRef.current.deviceController.stopAudioOutput();
            setIsCalling(false);
            meetingSessionRef.current = null; // MeetingSession をリセット
        } else {
            log("通話が開始されていません");
        }
    }, []);

    return { startWebRTCContact, endWebRTCContact, isCalling };
}
